import { getUserSaveError } from "../utils/userManagementErrors";
import "./UserManagement.css";
import UserManagementIcon from "./UserManagementIcon";
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const ResourceFormDialog = ({
  open,
  onClose,
  onSave,
  mode = "create",
  initialData = null,
}) => {
  const theme = useTheme();
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const saveErrorRef = useRef(null);
  useEffect(() => { if (open) setSaveError(""); }, [open, mode, initialData]);
  useEffect(() => { if (saveError) saveErrorRef.current?.focus(); }, [saveError]);
  const [formData, setFormData] = useState({
    resourceType: "",
    resourceKey: "",
    displayName: "",
    description: "",
  });

  useEffect(() => {
    if (open && mode === "edit" && initialData) {
      setFormData({
        resourceType: initialData.resourceType || "",
        resourceKey: initialData.resourceKey || "",
        displayName: initialData.displayName || "",
        description: initialData.description || "",
      });
    } else if (open && mode === "create") {
      setFormData({
        resourceType: "",
        resourceKey: "",
        displayName: "",
        description: "",
      });
    }
  }, [open, mode, initialData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaveError(""); setSaving(true);
    try { await onSave(formData); }
    catch (error) { setSaveError(getUserSaveError(error)); }
    finally { setSaving(false); }
  };

  const handleClose = () => {
    if (saving) return;
    setSaveError("");
    setFormData({
      resourceType: "",
      resourceKey: "",
      displayName: "",
      description: "",
    });
    onClose();
  };

  return (
    <Dialog
        className={`user-management-dialog ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          pb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <UserManagementIcon name="resource" className="user-management-title-icon" />
        {mode === "create" ? "Create Resource" : "Edit Resource"}
        <IconButton
          onClick={handleClose}
          aria-label="Close dialog"
          sx={{
            position: "relative",
            right: -8,
            top: -8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {saveError && <Alert severity="error" role="alert" tabIndex={-1} ref={saveErrorRef} sx={{mb:2}}>{saveError}</Alert>}
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {/* Resource Type */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Resource Type"
                value={formData.resourceType}
                onChange={(e) => handleChange("resourceType", e.target.value)}
                required
                variant="outlined"
                helperText="e.g., page, feature, element, record"
              />
            </Grid>

            {/* Resource Key */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Resource Key"
                value={formData.resourceKey}
                onChange={(e) => handleChange("resourceKey", e.target.value)}
                required
                variant="outlined"
              />
            </Grid>

            {/* Display Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => handleChange("displayName", e.target.value)}
                required
                variant="outlined"
                helperText="Human-readable name shown in UI"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          <UserManagementIcon name="cancel" /> Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: theme.palette.primary.main,
            "&:hover": { bgcolor: theme.palette.primary.dark },
          }}
        >
          <UserManagementIcon name="tick" /> Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceFormDialog;
